import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { backupLogAPI } from '../api/backupLog';
import { toast } from 'react-toastify';
import './BackupLogs.css';

const BackupLogs = () => {
  const { uniqueId } = useParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [uniqueId, pagination.page, statusFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await backupLogAPI.getByInstallation(
        uniqueId,
        pagination.page,
        pagination.limit,
        statusFilter
      );
      
      setLogs(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error(error.message || 'Failed to fetch backup logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { class: 'success', text: '✅ Success' },
      failed: { class: 'failed', text: '❌ Failed' },
      in_progress: { class: 'in-progress', text: '⏳ In Progress' },
      initiated: { class: 'initiated', text: '🔄 Initiated' },
      partial_success: { class: 'partial', text: '⚠️ Partial Success' },
      cancelled: { class: 'cancelled', text: '🚫 Cancelled' },
      pending: { class: 'pending', text: '⏸️ Pending' }
    };
    return badges[status] || { class: 'unknown', text: status };
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  if (loading) {
    return <div className="loading">Loading backup logs...</div>;
  }

  return (
    <div className="backup-logs-container">
      <div className="logs-header">
        <h1>Backup Logs</h1>
        <div className="header-actions">
          <button onClick={handleRefresh} className="btn btn-secondary">
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="filters">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="in_progress">In Progress</option>
          <option value="initiated">Initiated</option>
          <option value="partial_success">Partial Success</option>
        </select>
      </div>

      {logs.length === 0 ? (
        <div className="empty-state">
          <p>No backup logs found</p>
        </div>
      ) : (
        <>
          <div className="logs-table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Backup Config</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Start Time</th>
                  <th>Duration</th>
                  <th>Size</th>
                  <th>SFTP</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const statusBadge = getStatusBadge(log.status);
                  return (
                    <tr key={log._id}>
                      <td>
                        <div className="config-cell">
                          <strong>{log.backupConfig?.configName || 'N/A'}</strong>
                          {log.scheduleName && (
                            <span className="schedule-name">{log.scheduleName}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${statusBadge.class}`}>
                          {statusBadge.text}
                        </span>
                      </td>
                      <td>{log.backupType || 'full'}</td>
                      <td>{new Date(log.startTime).toLocaleString()}</td>
                      <td>{formatDuration(log.duration)}</td>
                      <td>
                        {log.backupFile?.sizeBytes
                          ? formatBytes(log.backupFile.sizeBytes)
                          : 'N/A'}
                      </td>
                      <td>
                        {log.sftpResults && log.sftpResults.length > 0 ? (
                          <span className="sftp-status">
                            {log.sftpResults.filter(r => r.status === 'success').length}/
                            {log.sftpResults.length}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        <Link
                          to={`/backup-log/${log._id}`}
                          className="btn btn-sm btn-outline"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn btn-sm"
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="btn btn-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BackupLogs;